import { useEffect, useMemo } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import request from 'utils/request';
import { generateFakeValue } from 'utils/mockFn';
import { showSnackbar, useMaskMode } from 'stores/useUiStore';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import type { ApiRespBody } from 'types/api';
import type { NewUser, User } from 'containers/User/user.types';

import type { GroupsData, UserGroup, UsersData } from '../security.types';


export const GROUPS_QUERY_KEY = ['securityGroups'] as const;
export const USERS_QUERY_KEY = ['securityUsers'] as const;

type UsersApiResponse = Omit<ApiRespBody, 'message'> & {
  message: string | User[];
};

export async function fetchGroups(
  authToken: string,
  signal?: AbortSignal
): Promise<UserGroup[]> {
  const result = await request<ApiRespBody<UserGroup[]>>('/api/groups', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success) return result.data ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load groups');
}

export async function fetchUsers(
  authToken: string,
  signal?: AbortSignal
): Promise<User[]> {
  const result = await request<UsersApiResponse>('/api/users', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success && Array.isArray(result.message)) return result.message;
  const message =
    typeof result?.message === 'string'
      ? result.message
      : 'Failed to load users';
  if (message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(message || 'Failed to load users');
}

export function groupsQueryOptions(
  authToken: string,
  organizationId: number | string | null,
  enabled = true
) {
  return queryOptions({
    queryKey: [...GROUPS_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchGroups(authToken, signal),
    staleTime: Infinity,
    enabled: enabled && !!authToken,
  });
}

export function usersQueryOptions(
  authToken: string,
  organizationId: number | string | null,
  enabled = true
) {
  return queryOptions({
    queryKey: [...USERS_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchUsers(authToken, signal),
    staleTime: Infinity,
    enabled: enabled && !!authToken,
  });
}

function useReadErrorEffect(error: unknown, caller: string) {
  useEffect(() => {
    if (!(error instanceof Error)) return;
    if (error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller,
          open: true,
          message: error.message,
          severity: 'error',
        });
    }
  }, [error, caller]);
}

export type UseGroupsResult = GroupsData & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export type UseUsersResult = UsersData & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export function useGroups(enabled = true): UseGroupsResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  const query = useQuery(
    groupsQueryOptions(authToken, organizationId, enabled)
  );

  useReadErrorEffect(query.error, 'onGroupsGet');

  const values = useMemo(() => query.data ?? [], [query.data]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: () => {
        query.refetch();
      },
    }),
    [values, lastUpdated, query]
  );
}

export function useUsers(enabled = true): UseUsersResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();
  const maskMode = useMaskMode();

  const query = useQuery(usersQueryOptions(authToken, organizationId, enabled));

  useReadErrorEffect(query.error, 'onUsersGet');

  const values = useMemo(() => {
    const data = query.data ?? [];
    if (!maskMode) return data;
    return data.map((u) => ({
      ...u,
      userName: generateFakeValue(u.userName, 'email'),
    }));
  }, [query.data, maskMode]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: () => {
        query.refetch();
      },
    }),
    [values, lastUpdated, query]
  );
}

function useSecurityWrite<TVars>(opts: {
  caller: string;
  successMessage: string;
  queryKey: readonly unknown[];
  mutationFn: (vars: TVars, authToken: string) => Promise<ApiRespBody | null>;
}) {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: TVars) => opts.mutationFn(vars, authToken),
    onSuccess: (result) => {
      if (result?.success) {
        showSnackbar({
            autoHideDuration: 5000,
            caller: opts.caller,
            open: true,
            message: opts.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: opts.queryKey });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: opts.caller,
            open: true,
            message: result?.message ?? 'Operation failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      // Swallow thrown errors with console.error.
      console.error(err);
    },
  });
}

export function useAddGroup() {
  return useSecurityWrite<UserGroup>({
    caller: 'onGroupAdd',
    successMessage: 'Group Added',
    queryKey: GROUPS_QUERY_KEY,
    mutationFn: (group, authToken) =>
      request<ApiRespBody>('/api/groups', {
        method: 'POST',
        body: JSON.stringify(group),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}

export function useUpdateGroup() {
  return useSecurityWrite<UserGroup>({
    caller: 'onGroupUpd',
    successMessage: 'Group Updated',
    queryKey: GROUPS_QUERY_KEY,
    mutationFn: (group, authToken) =>
      request<ApiRespBody>('/api/groups', {
        method: 'PUT',
        body: JSON.stringify(group),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}

export function useDeleteGroup() {
  return useSecurityWrite<UserGroup>({
    caller: 'onGroupDel',
    successMessage: 'Group Deleted',
    queryKey: GROUPS_QUERY_KEY,
    mutationFn: (group, authToken) =>
      request<ApiRespBody>('/api/groups', {
        method: 'DELETE',
        body: JSON.stringify(group),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}

export function useAddUser() {
  return useSecurityWrite<NewUser>({
    caller: 'onUserAdd',
    successMessage: 'User Added',
    queryKey: USERS_QUERY_KEY,
    mutationFn: (user, authToken) =>
      request<ApiRespBody>('/api/users', {
        method: 'POST',
        body: JSON.stringify(user),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}

export function useUpdateUser() {
  return useSecurityWrite<User>({
    caller: 'onUserUpdate',
    successMessage: 'User Updated',
    queryKey: USERS_QUERY_KEY,
    mutationFn: (user, authToken) =>
      request<ApiRespBody>(`/api/users/${user.userName}/security`, {
        method: 'PUT',
        body: JSON.stringify(user),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}

export function useDeleteUser() {
  return useSecurityWrite<User>({
    caller: 'onUserDel',
    successMessage: 'User Deleted',
    queryKey: USERS_QUERY_KEY,
    mutationFn: (user, authToken) =>
      request<ApiRespBody>(`/api/users/${user.userName}`, {
        method: 'DELETE',
        body: JSON.stringify(user),
        headers: { 'Content-Type': 'application/json', authorization: authToken },
      }),
  });
}
